import axios, { CancelTokenSource } from 'axios';
import { clearStorage, isInvalidTokenMessage, notifyInvalidTokenAndLogout, openNotification, redirectToLoginWithHardReload } from './utils';

const locale = 'tr-TR';

export const withoutAccessHttp = axios.create({
  baseURL: `${process.env.REACT_APP_API_BASE_URL}`,
  headers: {
    'Accept': '*/*',
    'content-type': 'application/json',
  }
});

const cancelTokens: CancelTokenSource[] = [];

export const http = axios.create({
  baseURL: `${process.env.REACT_APP_API_BASE_URL}`,
  headers: {
    'Accept': '*/*',
    'Authorization': 'Bearer ' + localStorage.getItem('accessToken'),
    'content-type': 'application/json',
  }
});

// A lightweight wrapper providing the previous apiFetch signature
export async function apiFetch(url: string, options: RequestInit = {}, ignoreAuth = false) {
  const client = ignoreAuth ? withoutAccessHttp : http;
  const method = (options.method || 'GET').toLowerCase();

  // Map Fetch options to Axios config
  const config: any = { url, method };
  if (options.headers) config.headers = options.headers as any;
  if (options.body) config.data = options.body as any;

  const res = await client.request(config);
  return res?.data;
}

// Request interceptor
http.interceptors.request.use(function (config: any) {
  const source = axios.CancelToken.source();
  config.cancelToken = source.token;
  cancelTokens.push(source);

  const token = localStorage.getItem('accessToken');
  const headers: Record<string, any> = { ...(config.headers || {}) };

  if (token || config.url === '/login' || config.url === '/refresh' || config.url === '/authorize/token') {
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    if (config.url === '/authorize/token') {
      headers['Content-Type'] = 'application/json';
      headers['accept-language'] = locale;
      headers['Access-Control-Allow-Origin'] = '*';
    }

    // Ensure JSON content-type for body methods when not FormData
    const method = (config.method || 'get').toLowerCase();
    const hasBody = ['post', 'put', 'patch', 'delete'].includes(method);
    if (hasBody && config.data !== undefined && !(config.data instanceof FormData)) {
      headers['Content-Type'] = headers['Content-Type'] || 'application/json';
    }

    config.headers = headers;
    return config;
  } else {
    redirectToLoginWithHardReload();
    throw new axios.Cancel('Operation canceled by the user.');
  }
}, function (error) {
  return Promise.reject(error);
});

// Response interceptor
http.interceptors.response.use(
  response => response,
  async error => {
    const responseMessage =
      typeof error?.response?.data === 'string'
        ? error.response.data
        : error?.response?.data?.message;

    if (isInvalidTokenMessage(responseMessage)) {
      notifyInvalidTokenAndLogout();
      return await new Promise<never>(() => {});
    }

    if (error.response && error.response.status === 401) {
      console.warn('401 Unauthorized hatası alındı, token yenileniyor...');

      const originalRequest = error.config;

      // Refresh token kullanılarak yeni access token alın
      const newAccessToken = await refreshAccessToken();

      if (newAccessToken) {
        // Yeniden isteği gönder
        originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
        return http(originalRequest);
      }
    }

    // Refresh token da başarısız olursa kullanıcıyı login sayfasına yönlendir
    if (error.response && error.response.status === 401) {
      openNotification('error', error.response.status, 'Giriş yapınız');
      cancelTokens.forEach(source => source.cancel('Operation canceled due to 401 error'));
      clearStorage();
      setTimeout(() => {
        redirectToLoginWithHardReload();
      }, 500);
    }

    return Promise.reject(error);
  }
);

const refreshAccessToken = async () => {
  try {
    const refreshToken = localStorage.getItem('refreshToken');
    if (!refreshToken) {
      throw new Error('Refresh token bulunamadı');
    }

    const response = await withoutAccessHttp.post('/auth/refresh', { refreshToken });

    // Yeni token'ları kaydet
    localStorage.setItem('accessToken', response.data.accessToken);
    localStorage.setItem('refreshToken', response.data.refreshToken);

    // Yeni token'ı header'a ekle
    http.defaults.headers['Authorization'] = `Bearer ${response.data.accessToken}`;
    return response.data.accessToken;
  } catch (error) {
    console.error('Refresh token yenileme başarısız:', error);
    clearStorage();
    redirectToLoginWithHardReload();
    return null;
  }
};
